import { useCallback, useMemo } from "react";

import { useFieldArray, useFormContext } from "react-hook-form";

import { Delete as DeleteIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import { formatNumber } from "@/features/shared";

export const ProductVariantsSection = () => {
  const { showSnackbar } = useSnackbar();
  const { control, setValue } = useFormContext();

  const { fields: customerClaimProductVariants, remove } = useFieldArray({
    control,
    name: "customerClaimProductVariants",
  });

  const handleDelete = useCallback(
    (id) => {
      const idx = customerClaimProductVariants.findIndex((v) => v.id === id);
      if (idx !== -1) {
        remove(idx);
      }
    },
    [customerClaimProductVariants, remove]
  );

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      // Coerce to numbers
      const quantity = Number(newRow.quantity);

      // Validate type/finite
      if (!Number.isFinite(quantity)) {
        throw new Error("Kuantitas tidak valid");
      }

      // Validate quantity is at least 1
      if (quantity < 1) {
        throw new Error("Kuantitas tidak boleh kurang dari 1");
      }

      // Find the index and update
      const idx = customerClaimProductVariants.findIndex(
        (v) => v.id === newRow.id
      );
      if (idx === -1) return oldRow;

      const updatedRow = {
        ...customerClaimProductVariants[idx],
        quantity,
        note: newRow.note,
      };

      setValue(`customerClaimProductVariants.${idx}`, updatedRow, {
        shouldDirty: true,
      });

      return updatedRow;
    },
    [customerClaimProductVariants, setValue]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Nama Barang",
        flex: 3,
        renderCell: ({ row: { name, sku } }) => (
          <ProductVariantNameCell name={name} sku={sku} />
        ),
      },
      {
        field: "quantity",
        headerName: "Kuantitas",
        type: "number",
        valueFormatter: (value) => formatNumber(value),
        flex: 1,
        editable: true,
      },
      {
        field: "unitOfMeasurement",
        headerName: "Satuan",
        flex: 1,
      },
      {
        field: "note",
        headerName: "Catatan",
        flex: 2,
        editable: true,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Aksi",
        getActions: ({ row }) => [
          <IconButton
            key="delete"
            onClick={() => handleDelete(row.id)}
            size="small"
            color="error"
          >
            <DeleteIcon />
          </IconButton>,
        ],
      },
    ],
    [handleDelete]
  );

  return (
    <DataGrid
      getRowHeight={() => "auto"}
      rows={customerClaimProductVariants}
      columns={columns}
      disableRowSelectionOnClick
      hideFooter
      processRowUpdate={handleProcessRowUpdate}
      onProcessRowUpdateError={(error) => {
        showSnackbar(error.message);
      }}
    />
  );
};
